import React from 'react';
import styles from './index.scss';
import { connect } from 'react-redux';
import * as selectors from '../../../page/prep-and-send/data/selectors';
import { StoreShape } from '../../../redux/namespaces';
import { defineMessages, injectIntl } from 'react-intl';

const DBX_SCRIPT_SRC = 'https://www.dropbox.com/static/api/2/dropins.js';
const DBX_SCRIPT_ID = 'dropbox-addon-js';

const messages = defineMessages({
  loading: {
    id: '',
    description: 'Message letting the user know that the content is loading',
    defaultMessage: 'Loading',
  },
});

type MoverSettings = {
  appKey: string,
  accountId: string,
  showFoldersOnly: boolean,
  initialFolderSelection?: string,
  onFolderSelected: (path: string) => void,
};

interface MoverConstructor {
  new(settings: MoverSettings): MoverConstructor
}

interface Dropbox {
  mount: (mover: MoverConstructor, elm: HTMLElement) => void,
  Mover: MoverConstructor,
  unmount: (mover: MoverConstructor) => void,
}

type FolderProps = {
  initialFolderSelection?: string,
  onFolderSelected: (newFolder: string) => void,
  elementId: string,
  integration: any,
  intl: any,
}

type FolderState = {
  isScriptReady: boolean,
}

class Folder extends React.Component<FolderProps, FolderState> {

  private dbxObj: Dropbox | undefined;

  private mover: any;

  constructor(props: FolderProps) {
    super(props);
    this.state = {
      isScriptReady: false,
    };
  }

  componentDidMount() {
    this.loadScript()
      .then(() => { // Wait until the remote script loads
        this.setState({ isScriptReady: true });
        this.initChooser();
      });
  }

  componentWillUnmount() {
    if (this.dbxObj && this.mover) {
      this.dbxObj.unmount(this.mover);
    }
  }

  initChooser() {
    const { integration, onFolderSelected, elementId, initialFolderSelection } = this.props;

    this.dbxObj = (window as any).Dropbox as Dropbox;

    if (integration) {
      const { externalAppKey, externalAccountId } = integration!.data;

      this.mover = new this.dbxObj.Mover({
        appKey: externalAppKey,
        accountId: externalAccountId,
        showFoldersOnly: true,
        onFolderSelected,
        initialFolderSelection: initialFolderSelection || '/',
      });

      const element = document.querySelector(`#${elementId}`) as HTMLElement;
      this.dbxObj.mount(this.mover, element);
    }
  }

  loadScript() {
    let scriptEl = (document.getElementById(DBX_SCRIPT_ID) as HTMLScriptElement);
    if (!scriptEl) {
      return new Promise(((resolve, reject) => {
        scriptEl = document.createElement('script') as HTMLScriptElement;
        scriptEl.src = DBX_SCRIPT_SRC;
        scriptEl.id = DBX_SCRIPT_ID;

        scriptEl.addEventListener('load', () => {
          resolve(undefined);
        });
        scriptEl.addEventListener('error', (e) => {
          reject(e);
        });
        document.body.appendChild(scriptEl);
      }));
    } else if ((window as any).Dropbox) {
      return Promise.resolve();
    } else {
      return new Promise(((resolve, reject) => {
        scriptEl.addEventListener('load', () => {
          resolve(undefined);
        });
        scriptEl.addEventListener('error', (e) => {
          reject(e);
        });
      }));
    }
  }

  render() {
    const { elementId, intl } = this.props;
    const { isScriptReady } = this.state;
    if (!isScriptReady) {
      return <div>{intl.formatMessage(messages.loading)}...</div>;
    }

    return (
      <div className={styles.moverFix}>
        <div
          id={elementId}
          className={styles.moverContainer}
          data-qa-ref={elementId} />
      </div>
    );
  }
}

const mapStateToProps = (state: StoreShape) => {
  return {
    integration: selectors.getIntegration(state),
  };
};

export default connect(
  mapStateToProps,
)(injectIntl(Folder));
